import { useTheme } from "next-themes";
import "./../styles/goalCard.css";
import { BusinessIcon, CarIcon, EducationIcon, MarriageIcon, MobileIcon, OtherIcon, PropertyIcon, RetirementIcon, TravelIcon } from "./icons";
import { numberWithCommas } from "../utils/numbers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

dayjs.extend(utc);
dayjs.extend(timezone);

const goalIcons = [
  {
    title: "CAR",
    icon: (width, height) => <CarIcon width={width} height={height} />,
  },
  {
    title: "MOBILE",
    icon: (width, height) => <MobileIcon width={width} height={height} />,
  },
  {
    title: "PROPERTY",
    icon: (width, height) => <PropertyIcon width={width} height={height} />,
  },
  {
    title: "EDUCATION",
    icon: (width, height) => <EducationIcon width={width} height={height} />,
  },
  {
    title: "MARRIAGE",
    icon: (width, height) => <MarriageIcon width={width} height={height} />,
  },
  {
    title: "RETIREMENT",
    icon: (width, height) => <RetirementIcon width={width} height={height} />,
  },
  {
    title: "TRAVEL",
    icon: (width, height) => <TravelIcon width={width} height={height} />,
  },
  {
    title: "BUSINESS",
    icon: (width, height) => <BusinessIcon width={width} height={height} />,
  },
  {
    title: "OTHER",
    icon: (width, height) => <OtherIcon width={width} height={height} />,
  },
];

const GoalCard = ({ data, onGoalSelect, getPortfolioPortion, getPortfolioSize }) => {
  const { theme } = useTheme();

  const { t } = useTranslation();

  if (!data && !data?.target_amount_real) return "";
  // Format numbers with proper currency formatting
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format date to "24 Sep, 2027" format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  /**
   * Compute goal progress percent (projected-final method).
   *
   * @param {object} opts
   * @param {number} opts.targetReal            -- target in today's rupees (authoritative)
   * @param {number} opts.currentBalanceReal    -- current goal balance in today's rupees
   * @param {number} opts.PMT_real              -- monthly contribution in today's rupees (canonical)
   * @param {string|Date} opts.asOf             -- "now" date (use dayjs-compatible)
   * @param {string|Date} opts.targetDate       -- target date (end date)
   * @param {number} opts.growthPct             -- annual growth percent (e.g. 12)
   * @param {number} opts.inflationPct          -- annual inflation percent (e.g. 5)
   * @returns {object} { progressPct, projectedFinalReal, iReal, monthsRemaining }
   */
  function computeGoalProgress({ targetReal, currentBalanceReal, PMT_real, asOf = new Date(), targetDate, growthPct = 0, inflationPct = 5, portfolioAmount, portfolioSize }) {
    // quick sanity
    targetReal = Number(targetReal || 0);
    currentBalanceReal = Number(currentBalanceReal || 0);
    PMT_real = Number(PMT_real || 0);

    if (targetReal <= 0) return { progressPct: 0, projectedFinalReal: 0, iReal: 0, monthsRemaining: 0 };

    // months remaining (fractional months not used; use month-end logic you prefer)
    const a = dayjs(asOf);
    const b = dayjs(targetDate);
    if (!b.isAfter(a)) {
      // target date is today or in past
      const projectedFinalReal = currentBalanceReal; // no future months -- optionally include one last payment
      return { progressPct: Math.min(100, (projectedFinalReal / targetReal) * 100), projectedFinalReal, iReal: 0, monthsRemaining: 0 };
    }

    // compute months remaining (whole months)
    let yearDiff = b.year() - a.year();
    let monthDiff = b.month() - a.month(); // month() is 0..11
    let totalMonths = yearDiff * 12 + monthDiff;
    if (b.date() < a.date()) totalMonths -= 1; // if target day-of-month is earlier, subtract one month
    if (totalMonths < 0) totalMonths = 0;
    const Nrem = totalMonths;

    // compute i_real (monthly)
    const r = Number(growthPct || 0) / 100;
    const f = Number(inflationPct || 0) / 100;
    const iReal = Math.pow((1 + r) / (1 + f), 1 / 12) - 1;

    // future value of annuity (real)
    let FV_annuity_real = 0;
    if (Nrem > 0) {
      // if (Math.abs(iReal) < 1e-12) {
        FV_annuity_real = PMT_real * Nrem;
      // } else {
        // FV_annuity_real = (PMT_real * (Math.pow(1 + iReal, Nrem) - 1)) / iReal;
      // }
    }

    // grow current balance to goal date (real)
    const balanceFuture = currentBalanceReal * Math.pow(1 + iReal, Nrem);

    const projectedFinalReal = balanceFuture + FV_annuity_real;

    const progressRatio = projectedFinalReal / targetReal;
    const progressPct = Math.round(Math.min(100, Math.max(0, progressRatio * 100)));

    return { progressPct, projectedFinalReal, iReal, monthsRemaining: Nrem, progressPctActual: Math.min(100, Math.max(0, Math.round(((portfolioAmount / portfolioSize) * 100)))) };
  }

  // Calculate progress percentage

  const args = {
   targetReal: data?.target_amount_real,
   currentBalanceReal: parseFloat(getPortfolioPortion(data?.portfolio_portion)),
   PMT_real: data?.monthly_induction_real,
   targetDate: data?.target_date,
   growthPct: Math.round(data?.portfolio_growth_rate * 100),
   inflationPct: Math.round(data?.annual_inflation_rate * 100),
   portfolioAmount: data?.portfolio_amount,
   portfolioSize: getPortfolioSize(),
  }

  const { progressPct, progressPctActual } = computeGoalProgress(args);
  // const progress = Math.min(Math.round(data.portfolio_portion * 100), 100);
  const goalIcon = goalIcons.find((x) => x.title === data.goal_category)?.icon(28, 28);

  return (
    <div className='card goal-card-container h-100 border-0' style={{ cursor: "pointer" }} onClick={() => onGoalSelect(data)}>
      <div className='goal-card h-100'>
        <div className='card-header d-flex align-items-center p-3'>
          <div className='me-3 bg-primary-gradient p-2 rounded'>{goalIcon ? goalIcon : ""}</div>
          <h3 className='card-title mb-0 text-uppercase fw-bold'>{t(`goals.modal.category.${data.goal_category?.toLowerCase()}`)}</h3>
        </div>

        <div className='card-body p-4'>
          <div className='row g-4'>
            <div className='col-6'>
              <div className='stat-label mb-1'>{t("goals.card.amount")}</div>
              <div className='stat-value fw-medium fs-5' style={{ letterSpacing: ".03em" }}>
                <span dir="ltr">
                  <span className='' dir="ltr" style={{ fontSize: "14px", marginRight: '.25rem', color: theme === "dark" ? "var(--theme-light-secondary" : "" }}>
                    {t("app.fund.rupee")}
                  </span>
                  <span dir="ltr">
                    {numberWithCommas(Math.round(parseFloat(data.target_amount_nominal)))}
                  </span>
                </span>
              </div>
            </div>

            <div className='col-6'>
              <div className='stat-label mb-1'>{t("goals.card.period")}</div>
              <div className='stat-value fw-medium fs-5'>
                {data.goal_duration}
                <span className='ms-1' style={{ fontSize: "14px", color: theme === "dark" ? "var(--theme-light-secondary" : "" }}>
                  {t("goals.common.year")}
                </span>
              </div>
            </div>

            <div className='col-6'>
              <div className='stat-label mb-1'>{t("goals.card.induction")}</div>
              <div className='stat-value fw-medium fs-5' style={{ letterSpacing: ".03em" }}>
                <span dir="ltr">
                  <span className='' dir="ltr" style={{ fontSize: "14px", marginRight: '.25rem', color: theme === "dark" ? "var(--theme-light-secondary" : "" }}>
                    {t("app.fund.rupee")}
                  </span>
                  <span dir="ltr">
                    {numberWithCommas(Math.round(parseFloat(data.monthly_induction_nominal)))}
                  </span>
                </span>
              </div>
            </div>

            <div className='col-6'>
              <div className='stat-label mb-1'>{t("goals.card.date")}</div>
              <div className='stat-value fw-medium fs-5'>{formatDate(data.target_date)}</div>
            </div>
          </div>

          <div className='mt-4'>
            <div className='d-flex justify-content-between mb-1'>
              <span className='small stat-progress-label'>{t("goals.card.progress")}</span>
              <span className={`fw-medium text-info`}>{progressPctActual}% {t("goals.card.complete")}</span>
            </div>

            <div className='progress' style={{ height: "10px" }}>
              <div className='progress-bar bg-gradient' role='progressbar' style={{ width: `${progressPctActual}%` }} aria-valuenow={progressPctActual} aria-valuemin='0' aria-valuemax='100' />
            </div>
          </div>

          <div
            className='mt-4 pt-3 border-top'
            style={{
              ["--bs-border-color"]: "var(--card-border)",
            }}
          >
            <div className='d-flex justify-content-between small'>
              <span className='stat-label'>{t("goals.card.portfolio")}:</span>
              <span className='fw-medium stat-value' style={{ letterSpacing: ".03em" }}>
                <span className='stat-label me-1' style={{ fontSize: "11px" }}>
                  Rs
                </span>
                {numberWithCommas(Math.round(parseFloat(data.portfolio_amount)))}
              </span>
            </div>
            <div className='d-flex justify-content-between small mt-1'>
              <span className='stat-label'>{t("goals.card.growth")}:</span>
              <span className={`fw-medium ${data.portfolio_growth_rate > 0.1 ? "text-success" : "text-primary"}`}>
                {(data.portfolio_growth_rate * 100).toFixed(1)}
                <span className='ms-1' style={{ fontSize: "11px" }}>
                  %
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Usage Example
const GoalCards = () => {
  const goals = [
    {
      amc_code: "DL",
      folio_no: "DL-9901",
      email: "thomas.clark@yopmail.com",
      portfolio_portion: 0.5,
      portfolio_growth_rate: 0.12,
      portfolio_amount: "73156.7666",
      annual_inflation_rate: 0.05,
      goal_duration: 10,
      target_amount_real: "1000000",
      target_amount_nominal: "1628894.6268",
      goal_category: "CAR",
      portfolios_inlcuded: "[]",
      monthly_induction_nominal: "8858.1926",
      monthly_induction_real: "5117.8604",
      target_date: "2035-09-23T09:09:47.777Z",
      investor_bank_name: "MBL",
      investor_branch_name: "ise",
      investor_bank_iban: "JAF 43535345",
      investor_account_title: "ABC",
    },
  ];

  return (
    <div className='container goal-container py-5'>
      <div className='row g-4'>
        {goals.map((goal, index) => (
          <div key={index} className='col-12 col-md-6'>
            <div className='card goal-card-container h-100 border-0'>
              <GoalCard goal={goal} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GoalCard;
